import React, { useCallback, useEffect, useRef } from 'react';
import {
  AmplifyCore,
  loggerFactory,
  sessionManagement,
  ActivityMonitor
} from '@codametrix/ui-common';
import { broadcastWindowActivity } from '../../core/session-activity';

const providerFrameLogger = loggerFactory('ProviderDirectoryFrame');

const MESSAGE_TYPES = {
  PROVIDER_WIDGET_READY: 'PROVIDER_WIDGET_READY',
  IFRAME_ACTIVITY: 'IFRAME_ACTIVITY',
  PROVIDER_DIRECTORY_TOKEN: 'PROVIDER_DIRECTORY_TOKEN',
} as const;

const PROVIDER_DIRECTORY_URL = '/app/provider-directory/';

const ProviderDirectoryFrame: React.FC = () => {
  const iframeTargetRef = useRef<MessageEventSource | null>(null);
  const iframeOriginRef = useRef<string | null>(null);

  const sendToken = useCallback(async (target: MessageEventSource, origin: string) => {
    try {
      const token = await AmplifyCore.fetchXCaliberToken();
      (target as Window).postMessage({ type: MESSAGE_TYPES.PROVIDER_DIRECTORY_TOKEN, ...token }, origin);
    } catch (err) {
      providerFrameLogger.error('Failed to fetch Xcaliber token', err);
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.data?.type === MESSAGE_TYPES.PROVIDER_WIDGET_READY) {
        iframeTargetRef.current = event.source;
        iframeOriginRef.current = event.origin;
        if (event.source) {
          sendToken(event.source, event.origin);
        }
      } else if (event.data?.type === MESSAGE_TYPES.IFRAME_ACTIVITY) {
        // Iframe input is local to this browsing context — record for heartbeat.
        broadcastWindowActivity();
      }
    };

    const refreshHandler = async () => {
      const target = iframeTargetRef.current;
      const origin = iframeOriginRef.current;
      if (target && origin) {
        await sendToken(target, origin);
      }
    };

    window.addEventListener('message', handleMessage);
    sessionManagement.activityMonitor.on(
      ActivityMonitor.events.DID_REFRESH,
      refreshHandler
    );

    return () => {
      window.removeEventListener('message', handleMessage);
      sessionManagement.activityMonitor.removeListener(
        ActivityMonitor.events.DID_REFRESH,
        refreshHandler
      );
      iframeTargetRef.current = null;
      iframeOriginRef.current = null;
    };
  }, [sendToken]);

  return (
    <div
      style={{
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flex: 1,
        width: '100%',
      }}
    >
      <iframe
        src={PROVIDER_DIRECTORY_URL}
        title="Provider Directory"
        style={{
          width: '100%',
          minHeight: '100vh',
          border: 'none',
        }}
      />
    </div>
  );
};

export { ProviderDirectoryFrame };
